import React, { useContext } from 'react';
import { Divider, Text, Box, Flex, AbsoluteCenter } from '@chakra-ui/react';

import { getParameterDisplayName, formatDate, getRelativeTime, getParameterUnit, getObservationInPreferredUnits, getLatestObservation } from '../../utils/helpers';
import { TIDE_PARAMETER } from '../../utils/constants';
import { ParameterChart } from './ParameterChart';
import { TideTrend } from './TideTrend';
import { PreferredUnitsContext } from '../contexts/PreferredUnitsContext';

const TidalBuoyData = (props) => {
    const { stationObservations, stationMetadata } = props;
    const [preferredUnitSystem] = useContext(PreferredUnitsContext);

    const tidalObservations = [];
    const unit = getParameterUnit(stationMetadata, TIDE_PARAMETER);

    stationObservations.forEach((obs, index) => {
        const displayName = getParameterDisplayName(stationMetadata, TIDE_PARAMETER);
        const convertedObservation = getObservationInPreferredUnits(TIDE_PARAMETER, obs[TIDE_PARAMETER], unit, preferredUnitSystem);

        const time = formatDate(obs.time);

        const observation = {
            standardName: TIDE_PARAMETER,
            displayName: displayName,
            value: convertedObservation.value,
            unit: convertedObservation.unit,
            time: time,
            timestamp: obs.time,
        };

        if (index > 0) {
            const previousValue = tidalObservations[index - 1].value;
            if (convertedObservation.value < previousValue) {
                observation.trend = 'decreasing';
            } else if (convertedObservation.value > previousValue) {
                observation.trend = 'increasing';
            } else {
                observation.trend = 'stable';
            }
        }

        tidalObservations.push(observation);
    });

    const latestObservation = getLatestObservation(stationObservations, TIDE_PARAMETER);
    const convertedObservation = getObservationInPreferredUnits(TIDE_PARAMETER, latestObservation[TIDE_PARAMETER], unit, preferredUnitSystem);

    const currentTidalObservation = {
        value: convertedObservation.value,
        unit: convertedObservation.unit,
        time: getRelativeTime(latestObservation.time)
    };

    // sort by time descending (newest to oldest)
    tidalObservations.sort((a, b) => {
        const timeA = new Date(a.timestamp);
        const timeB = new Date(b.timestamp);
        return timeB - timeA;
    });

    const chartData = stationObservations
        .filter((observation) => observation[TIDE_PARAMETER])
        .map((observation) => ({
            [TIDE_PARAMETER]: observation[TIDE_PARAMETER],
            time: observation.time,
        }));

    return (
        <Box pt={4} px={4} bg={'white'}>
            <Text fontSize={16} fontWeight={600} color='gray.700'>
                {getParameterDisplayName(stationMetadata, TIDE_PARAMETER)}
            </Text>
            {currentTidalObservation && (
                <Box py={0}>
                    <Text fontSize={16} fontWeight={700} color='gray.700'>
                        {currentTidalObservation.value}
                        {currentTidalObservation.unit}
                        <span style={{ fontStyle: 'italic', fontSize: 12, fontWeight: 400, color: '#718096' }}>{' '}Latest</span>
                    </Text>
                    <Text color='gray.700' fontWeight='400' fontSize={14} pt={2}>
                        {currentTidalObservation.time}
                    </Text>
                </Box>
            )}
            <ParameterChart parameter={TIDE_PARAMETER} stationMetadata={stationMetadata} parameterObservations={chartData} simpleChart={false} />
            <Box position='relative' paddingTop='1' paddingBottom='1'>
                <Divider />
                <AbsoluteCenter px='4' bg='white' textColor={'gray.400'} marginBottom={20}>
                    Newest to Oldest
                </AbsoluteCenter>
            </Box>
            {tidalObservations.map((observation, index) => (
                <div key={index} style={{ marginTop: 20 }}>
                    <Box py={4}>
                        <Flex justifyContent='space-between' alignItems='center'>
                            <Text display='flex' justifyContent='center' alignItems='center'>
                                {observation?.trend && <TideTrend trend={observation.trend} />}
                                {observation.time}
                            </Text>
                            <Box textAlign='center' p={0}>
                                <Text fontWeight={600}>{observation.value}</Text>
                                <Text>{observation.unit}</Text>
                            </Box>
                        </Flex>
                    </Box>
                    <Divider />
                </div>
            ))}
        </Box>
    );
};

export default TidalBuoyData;
